import React, { useState, useEffect } from "react";
import {
  FaHourglassHalf,
  FaCheckCircle,
  FaTimesCircle,
  FaEye,
  FaBullhorn,
  FaChartBar,
  FaSync,
  FaArrowUp,
  FaArrowDown,
  FaUsers,
  FaPlay,
  FaPause,
  FaStop,
  FaFilter,
  FaSearch,
  FaEdit
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const RaiseVoiceStatus = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [voices, setVoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [stats, setStats] = useState({
    underReview: 0,
    approved: 0,
    accepted: 0,
    rejected: 0,
    running: 0,
    modified: 0,
  });

  useEffect(() => {
    const fetchVoices = async () => {
      try {
        setLoading(true);

        const allVoicesResponse = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/get/all/ryv/ad/stats`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Fetched Voices Data:", allVoicesResponse);
        // If API returns stats object, use it directly
        const statsData = allVoicesResponse.data.stats || {};
        setStats({
          underReview: statsData.underReview || 0,
          approved: statsData.approved || 0,
          accepted: statsData.accepted || 0,
          rejected: statsData.rejected || 0,
          running: statsData.running || 0,
          modified: statsData.modified || 0,
        });
        // If you still want to keep voices list for the table, set it if available
        const allVoicesData = allVoicesResponse.data.data || [];
        setVoices(allVoicesData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching voices:", error);
        // toast.error("Failed to load voices");
        setLoading(false);
      }
    };
    fetchVoices();
  }, []);

  // Handle card click to navigate to specific pages
  const handleCardClick = (type) => {
    switch (type) {
      case "pending":
        navigate("/admin/pendingvoice");
        break;
      case "approved":
        navigate("/admin/approvedvoice");
        break;
      case "accepted":
        navigate("/admin/runningvoice");
        break;
      case "rejected":
        navigate("/admin/rejectedvoice");
        break;
      case "running":
        navigate("/admin/runningvoice");
        break;
      case "modified":
        navigate("/admin/modifiedvoice");
        break;

      default:
        break;
    }
  };

  // Filter voices based on search term and status
  const filteredVoices = voices.filter(voice => {
    const matchesSearch = voice.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      voice.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      voice._id?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === "all" || voice.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Calculate total voices
  const totalVoices = voices.length;
  const filteredCount = filteredVoices.length;

  // StatCard component
  const StatCard = ({ icon: Icon, title, value, color, onClick, trend, trendValue, description }) => (
    <div
      className={`group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 ${color} ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
      <div className="relative p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <Icon className="text-white text-xl" />
            </div>
            <div>
              <p className="text-white/90 text-sm font-medium">{title}</p>
              {description && (
                <p className="text-white/70 text-xs mt-1">{description}</p>
              )}
            </div>
          </div>
          {trend && (
            <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${trend === 'up' ? 'bg-green-500/20 text-green-100' : 'bg-red-500/20 text-red-100'
              }`}>
              {trend === 'up' ? <FaArrowUp className="w-3 h-3" /> : <FaArrowDown className="w-3 h-3" />}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        <div className="flex items-end justify-between">
          <h3 className="text-3xl font-bold text-white">{value}</h3>
          <div className="text-white/60 text-sm">
            {onClick && (
              <div className="flex items-center space-x-1 group-hover:text-white transition-colors">
                <span>View</span>
                <FaEye className="w-3 h-3" />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-white/30 to-transparent"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header Section */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 rounded-2xl mx-4 mt-4 p-8 shadow-2xl">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center space-x-4 mb-6 lg:mb-0">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <FaBullhorn className="text-white text-2xl" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Raise Your Voice Status</h1>
              <p className="text-purple-100 text-lg">Monitor and manage voice submissions and campaigns</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3">
              <div className="text-white/80 text-sm">Total Voices</div>
              <div className="text-white font-semibold">{totalVoices}</div>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl px-4 py-3 transition-colors duration-300 flex items-center space-x-2 text-white"
            >
              <FaSync className="w-4 h-4" />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6 space-y-8">
        {/* Status Cards */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
              <FaChartBar className="text-purple-500" />
              <span>Voice Statistics</span>
            </h2>
            <div className="text-sm text-gray-500">Real-time data</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            <StatCard
              icon={FaHourglassHalf}
              title="Pending Voices"
              value={loading ? "..." : stats.underReview}
              color="bg-gradient-to-br from-yellow-500 to-orange-500"
              description="Under review"
              trend="up"
              trendValue="5%"
              onClick={() => handleCardClick("pending")}
            />
            <StatCard
              icon={FaCheckCircle}
              title="Tracking raise voices"
              value={loading ? "..." : stats.approved}
              color="bg-gradient-to-br from-green-500 to-emerald-600"
              description="Admin approved"
              trend="up"
              trendValue="12%"
              onClick={() => handleCardClick("approved")}
            />
            <StatCard
              icon={FaTimesCircle}
              title="Rejected Voices"
              value={loading ? "..." : stats.rejected}
              color="bg-gradient-to-br from-red-500 to-pink-600"
              description="Admin rejected"
              trend="down"
              trendValue="3%"
              onClick={() => handleCardClick("rejected")}
            />
            <StatCard
              icon={FaUsers}
              title="Running Accepted by Reporter"
              value={loading ? "..." : stats.running}
              color="bg-gradient-to-br from-teal-500 to-cyan-600"
              description="Reporter accepted"
              trend="up"
              trendValue="15%"
              onClick={() => handleCardClick("running")}
            />
            <StatCard
              icon={FaEdit}
              title="Modified Voices"
              value={loading ? "..." : stats.modified}
              color="bg-gradient-to-br from-purple-500 to-indigo-600"
              description="Admin modified"
              trend="up"
              trendValue="8%"
              onClick={() => handleCardClick("modified")}
            />
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 lg:mb-0 flex items-center space-x-2">
              <FaSearch className="text-purple-500" />
              <span>All Voices ({filteredCount} of {totalVoices})</span>
            </h3>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search voices..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 w-full sm:w-64"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="all">All Status</option>
                <option value="pending">Under Review</option>
                <option value="approved">Approved</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
                <option value="running">Running</option>
              </select>
            </div>
          </div>

          {/* Voices Table */}
          {!loading && filteredVoices.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-xl shadow-sm border border-gray-100">
                <thead>
                  <tr className="bg-gradient-to-r from-purple-50 to-pink-50 text-sm text-gray-700">
                    <th className="px-6 py-4 text-left font-semibold">Submitted</th>
                    <th className="px-6 py-4 text-left font-semibold">Voice ID</th>
                    <th className="px-6 py-4 text-left font-semibold">End Date</th>
                    <th className="px-6 py-4 text-left font-semibold">Title</th>
                    <th className="px-6 py-4 text-left font-semibold">Category</th>
                    <th className="px-6 py-4 text-left font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVoices.map((voice) => (
                    <tr key={voice._id} className="border-t hover:bg-gray-50 transition-colors duration-200">
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {voice.createdAt ? new Date(voice.createdAt).toLocaleDateString() : "-"}
                      </td>
                      <td className="px-6 py-4 text-sm font-mono text-gray-500">
                        {voice._id?.substring(0, 8)}...
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {voice.endDate ? new Date(voice.endDate).toLocaleDateString() : "-"}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {voice.title || "-"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {voice.category || "-"}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${voice.status === "approved"
                              ? "bg-green-100 text-green-800"
                              : voice.status === "rejected"
                                ? "bg-red-100 text-red-800"
                                : voice.status === "running"
                                  ? "bg-blue-100 text-blue-800"
                                  : voice.status === "accepted"
                                    ? "bg-teal-100 text-teal-800"
                                    : "bg-yellow-100 text-yellow-800"
                            }`}
                        >
                          {voice.status === "pending" ? "Under Review" : voice.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : !loading && filteredVoices.length === 0 ? (
            <div className="text-center py-12">
              <FaBullhorn className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No voices found</h3>
              <p className="text-gray-500">Try adjusting your search or filter criteria</p>
            </div>
          ) : loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
              <p className="text-gray-500">Loading voices...</p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default RaiseVoiceStatus;
